(() => {
  const ready = (fn) => {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  };

  ready(() => {
    initSwipers();
    initHeader();
    initMobileNav();
    initAnnouncement();
    initSearchModal();
    initCartDrawer();
    initCart();
    initProductGallery();
    initQuantityControls();
    initProductForm();
    initFilters();
    initAboutPage();
    initDiscountForm();
    initWordRotate();
    initPredictiveSearch();
    initRecommendations();
    initRecentlyViewed();
    initShare();
    initGiftToggle();
    initLocalization();
    initAccount();
    updateShippingMeter();
  });

  function initSwipers() {
    if (typeof Swiper === 'undefined') return;

    document.querySelectorAll('.swiper-products').forEach((el) => {
      new Swiper(el, {
        slidesPerView: 1.15,
        spaceBetween: 16,
        navigation: {
          nextEl: el.querySelector('.swiper-button-next'),
          prevEl: el.querySelector('.swiper-button-prev'),
        },
        breakpoints: {
          576: { slidesPerView: 2.1 },
          768: { slidesPerView: 3.1 },
          1200: { slidesPerView: 4 },
        },
      });
    });

    document.querySelectorAll('.swiper-testimonials').forEach((el) => {
      new Swiper(el, {
        slidesPerView: 1,
        spaceBetween: 18,
        autoplay: { delay: 5200, disableOnInteraction: false },
        pagination: { el: el.querySelector('.swiper-pagination'), clickable: true },
        breakpoints: {
          768: { slidesPerView: 2 },
          1200: { slidesPerView: 3 },
        },
      });
    });
  }

  function initHeader() {
    const header = document.querySelector('.site-header');
    if (!header) return;
    const onScroll = () => header.classList.toggle('scrolled', window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  function initMobileNav() {
    const toggle = document.querySelector('.mobile-toggle');
    const nav = document.querySelector('.mobile-nav');
    const close = document.querySelector('.mobile-nav-close');
    const overlay = document.querySelector('[data-mobile-overlay]');
    if (!toggle || !nav) return;

    const open = () => {
      nav.classList.add('open');
      overlay?.classList.add('open');
      overlay?.removeAttribute('hidden');
      toggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    };
    const shut = () => {
      nav.classList.remove('open');
      overlay?.classList.remove('open');
      overlay?.setAttribute('hidden', '');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    };

    toggle.addEventListener('click', open);
    close?.addEventListener('click', shut);
    overlay?.addEventListener('click', shut);
    nav.querySelectorAll('a').forEach((a) => a.addEventListener('click', shut));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && nav.classList.contains('open')) shut();
    });
  }

  function initAnnouncement() {
    const bar = document.querySelector('[data-announcement-bar]');
    const close = bar?.querySelector('[data-announcement-close]');
    if (!bar || !close) return;
    if (sessionStorage.getItem('evryn-announcement-closed') === '1') {
      bar.hidden = true;
      return;
    }
    close.addEventListener('click', () => {
      bar.hidden = true;
      sessionStorage.setItem('evryn-announcement-closed', '1');
    });
  }

  function initSearchModal() {
    const openBtn = document.querySelector('[data-search-open]');
    const modal = document.querySelector('[data-search-modal]');
    const closeBtn = modal?.querySelector('[data-search-close]');
    const input = modal?.querySelector('input[type="search"]');
    if (!openBtn || !modal) return;

    const close = () => {
      modal.classList.remove('open');
      document.body.style.overflow = '';
    };

    openBtn.addEventListener('click', (e) => {
      e.preventDefault();
      modal.classList.add('open');
      document.body.style.overflow = 'hidden';
      setTimeout(() => input?.focus(), 50);
    });
    closeBtn?.addEventListener('click', close);
    modal.addEventListener('click', (e) => { if (e.target === modal) close(); });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('open')) close();
    });
  }

  function drawerEls() {
    return {
      drawer: document.querySelector('[data-cart-drawer]'),
      overlay: document.querySelector('[data-cart-overlay]'),
    };
  }

  function openCartDrawer() {
    const { drawer, overlay } = drawerEls();
    if (!drawer) return;
    drawer.classList.add('open');
    drawer.setAttribute('aria-hidden', 'false');
    overlay?.classList.add('open');
    overlay?.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
  }

  function closeCartDrawer() {
    const { drawer, overlay } = drawerEls();
    drawer?.classList.remove('open');
    drawer?.setAttribute('aria-hidden', 'true');
    overlay?.classList.remove('open');
    overlay?.setAttribute('hidden', '');
    document.body.style.overflow = '';
  }

  function initCartDrawer() {
    document.querySelectorAll('[data-cart-open]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        openCartDrawer();
      });
    });
    document.querySelector('[data-cart-close]')?.addEventListener('click', closeCartDrawer);
    document.querySelector('[data-cart-overlay]')?.addEventListener('click', closeCartDrawer);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeCartDrawer();
    });
  }

  function initCart() {
    document.querySelectorAll('[data-add-to-cart]').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const variantId = btn.dataset.variantId;
        if (!variantId) {
          window.location.href = btn.dataset.productUrl || '/collections/all';
          return;
        }
        const qtyRoot = btn.closest('.product-info, .product-card, form, .sticky-atc');
        const qtyInput = qtyRoot?.querySelector('.qty-control input, [name="quantity"]');
        const qty = parseInt(qtyInput?.value || btn.dataset.qty || '1', 10);
        btn.disabled = true;
        btn.classList.add('loading');
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: Number(variantId), quantity: qty }),
          });
          const data = await response.json();
          if (!response.ok) {
            showToast(data.description || data.message || 'Could not add to cart', 'error');
            return;
          }
          await refreshCart();
          openCartDrawer();
          showToast('Added to bag', 'success');
        } catch {
          showToast('Something went wrong', 'error');
        } finally {
          btn.disabled = false;
          btn.classList.remove('loading');
        }
      });
    });

    document.querySelectorAll('[data-remove-from-cart]').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const key = btn.dataset.lineKey;
        if (!key) return;
        try {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity: 0 }),
          });
          window.location.reload();
        } catch {
          showToast('Could not remove item', 'error');
        }
      });
    });

    document.querySelectorAll('[data-update-qty]').forEach((input) => {
      input.addEventListener('change', async () => {
        const key = input.dataset.lineKey;
        const quantity = parseInt(input.value, 10);
        if (!key) return;
        try {
          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity }),
          });
          if (response.ok) window.location.reload();
        } catch {
          showToast('Could not update quantity', 'error');
        }
      });
    });
  }

  async function refreshCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      updateCartCount(cart.item_count || 0);
      const subtotal = document.querySelector('[data-cart-subtotal]');
      if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
      const items = document.querySelector('[data-cart-drawer-items]');
      if (items && cart.items) {
        items.innerHTML = cart.items.length
          ? cart.items.map((item) => {
              const src = item.image ? (item.image.includes('?') ? `${item.image}&width=240` : `${item.image}?width=240`) : '';
              return `
              <div class="cart-item">
                <div class="cart-item-image">${src ? `<img class="cart-item-photo" src="${src}" alt="">` : ''}</div>
                <div>
                  <h3 class="cart-item-name">${item.product_title}</h3>
                  <p class="cart-item-variant">${formatMoney(item.final_line_price)}</p>
                </div>
              </div>`;
            }).join('')
          : `<div class="cart-empty"><h3>Your bag is empty</h3></div>`;
      }
      const checkout = document.querySelector('.cart-drawer [name="checkout"]');
      if (checkout) checkout.disabled = !cart.item_count;
      updateShippingMeter(cart.total_price);
    } catch {
      /* ignore */
    }
  }

  function updateCartCount(count) {
    document.querySelectorAll('.cart-count').forEach((badge) => {
      badge.textContent = count;
      badge.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  function updateShippingMeter(totalCents) {
    const bar = document.querySelector('[data-shipping-bar]');
    const msg = document.querySelector('[data-shipping-message]');
    if (!bar) return;
    const threshold = Number(bar.dataset.threshold || 99900);
    const total = typeof totalCents === 'number' ? totalCents : 0;
    const pct = Math.min(100, Math.round((total / threshold) * 100) || 0);
    bar.style.width = `${pct}%`;
    if (msg && total >= threshold) msg.textContent = 'You have unlocked free shipping';
  }

  function initProductGallery() {
    const gallery = document.querySelector('[data-product-gallery]');
    if (!gallery) return;
    const slides = [...gallery.querySelectorAll('.gallery-slide')];
    const thumbs = [...gallery.querySelectorAll('[data-gallery-thumbs] [data-media-id]')];
    if (!slides.length) return;

    const show = (id) => {
      slides.forEach((slide) => slide.classList.toggle('is-active', slide.dataset.mediaId === String(id)));
      thumbs.forEach((thumb) => thumb.classList.toggle('is-active', thumb.dataset.mediaId === String(id)));
      const active = slides.find((s) => s.classList.contains('is-active'));
      const stage = gallery.querySelector('[data-gallery-stage]');
      if (stage && active?.dataset.aspect) {
        stage.style.setProperty('--media-ratio', active.dataset.aspect);
      }
      active?.querySelector('video')?.play?.();
      slides.forEach((s) => {
        if (!s.classList.contains('is-active')) s.querySelector('video')?.pause?.();
      });
    };

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => show(thumb.dataset.mediaId));
    });

    const step = (dir) => {
      const index = slides.findIndex((s) => s.classList.contains('is-active'));
      const next = slides[(index + dir + slides.length) % slides.length];
      if (next) show(next.dataset.mediaId);
    };
    gallery.querySelector('[data-gallery-prev]')?.addEventListener('click', () => step(-1));
    gallery.querySelector('[data-gallery-next]')?.addEventListener('click', () => step(1));

    const lightbox = document.querySelector('[data-lightbox]');
    const lightboxImg = lightbox?.querySelector('[data-lightbox-image]');
    gallery.querySelectorAll('[data-zoom-open]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const slide = btn.closest('.gallery-slide');
        const src = slide?.dataset.zoom;
        if (!src || !lightbox || !lightboxImg) return;
        lightboxImg.src = src;
        lightbox.hidden = false;
        document.body.style.overflow = 'hidden';
      });
    });
    const closeLightbox = () => {
      if (!lightbox) return;
      lightbox.hidden = true;
      document.body.style.overflow = '';
    };
    lightbox?.querySelector('[data-lightbox-close]')?.addEventListener('click', closeLightbox);
    lightbox?.addEventListener('click', (e) => { if (e.target === lightbox) closeLightbox(); });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && lightbox && !lightbox.hidden) closeLightbox();
    });

    window.EVRYN_SHOW_MEDIA = show;
  }

  function initQuantityControls() {
    document.querySelectorAll('.qty-control').forEach((control) => {
      const input = control.querySelector('input[type="number"]');
      const minus = control.querySelector('[data-qty-minus]');
      const plus = control.querySelector('[data-qty-plus]');
      if (!input) return;
      const min = parseInt(input.min || '1', 10);
      const max = parseInt(input.max || '99', 10);
      const syncState = () => {
        let value = parseInt(input.value, 10);
        if (Number.isNaN(value)) value = min;
        value = Math.min(max, Math.max(min, value));
        input.value = value;
        minus?.toggleAttribute('disabled', value <= min);
        plus?.toggleAttribute('disabled', value >= max);
      };
      minus?.addEventListener('click', () => {
        input.value = Math.max(min, parseInt(input.value, 10) - 1);
        syncState();
        input.dispatchEvent(new Event('change'));
      });
      plus?.addEventListener('click', () => {
        input.value = Math.min(max, parseInt(input.value, 10) + 1);
        syncState();
        input.dispatchEvent(new Event('change'));
      });
      input.addEventListener('change', syncState);
      input.addEventListener('blur', syncState);
      syncState();
    });
  }

  function initProductForm() {
    const form = document.querySelector('[data-product-form]');
    if (!form) return;
    const variantInput = form.querySelector('[name="id"]');
    const addBtn = form.querySelector('[data-product-add]');
    const priceEl = document.querySelector('[data-product-price]');
    const compareEl = document.querySelector('[data-product-compare]');
    const variants = window.EVRYN_VARIANTS || [];

    form.querySelectorAll('input[type="radio"][data-option]').forEach((radio) => {
      radio.addEventListener('change', () => {
        const selected = {};
        form.querySelectorAll('input[type="radio"][data-option]:checked').forEach((r) => {
          selected[r.dataset.option] = r.value;
        });
        const match = variants.find((v) => v.options.every((opt, i) => selected[`option${i + 1}`] === opt));
        if (!match) return;
        if (variantInput) variantInput.value = match.id;
        document.querySelectorAll('[data-add-to-cart], [data-product-add]').forEach((btn) => {
          btn.dataset.variantId = match.id;
          btn.disabled = !match.available;
        });
        if (addBtn) addBtn.textContent = match.available ? 'Add to bag' : 'Sold Out';
        if (priceEl) priceEl.textContent = formatMoney(match.price);
        if (compareEl) {
          if (match.compare_at_price > match.price) {
            compareEl.textContent = formatMoney(match.compare_at_price);
            compareEl.hidden = false;
          } else compareEl.hidden = true;
        }
        const sku = document.querySelector('[data-product-sku]');
        if (sku && match.sku) sku.textContent = `SKU: ${match.sku}`;
        const sticky = document.querySelector('[data-sticky-price]');
        if (sticky) sticky.textContent = formatMoney(match.price);
        const inventory = document.querySelector('[data-inventory]');
        if (inventory) {
          inventory.textContent = match.available ? 'In stock' : 'Out of stock';
        }
        form.querySelectorAll('[data-option-label]').forEach((label) => {
          const fieldset = label.closest('fieldset');
          const checked = fieldset?.querySelector('input:checked');
          if (checked) label.textContent = checked.value;
        });
        if (match.featured_media_id && window.EVRYN_SHOW_MEDIA) {
          window.EVRYN_SHOW_MEDIA(match.featured_media_id);
        }
      });
    });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (addBtn) { addBtn.disabled = true; addBtn.classList.add('loading'); }
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: new FormData(form),
        });
        const data = await response.json();
        if (!response.ok) {
          showToast(data.description || 'Could not add to cart', 'error');
          return;
        }
        await refreshCart();
        openCartDrawer();
        showToast('Added to bag', 'success');
      } catch {
        showToast('Something went wrong', 'error');
      } finally {
        if (addBtn) { addBtn.disabled = false; addBtn.classList.remove('loading'); }
      }
    });
  }

  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.themeMoneyFormat || '{{amount}}');
    }
    return (cents / 100).toFixed(2);
  }

  function initFilters() {
    document.querySelectorAll('.products-filter-form').forEach((form) => {
      form.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
        checkbox.addEventListener('change', () => form.submit());
      });
    });
    const sortSelect = document.querySelector('[data-sort-select]');
    sortSelect?.addEventListener('change', () => { window.location.href = sortSelect.value; });
    const toggle = document.querySelector('.filter-toggle');
    const panel = document.getElementById('productsFiltersMobile');
    if (!toggle || !panel) return;
    toggle.addEventListener('click', () => {
      const isHidden = panel.hasAttribute('hidden');
      if (isHidden) { panel.removeAttribute('hidden'); toggle.setAttribute('aria-expanded', 'true'); }
      else { panel.setAttribute('hidden', ''); toggle.setAttribute('aria-expanded', 'false'); }
    });
  }

  function initDiscountForm() {
    const form = document.querySelector('[data-discount-form]');
    if (!form) return;
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const code = form.querySelector('input[name="discount"]')?.value?.trim();
      if (!code) { showToast('Please enter a coupon code.', 'error'); return; }
      window.location.href = `/discount/${encodeURIComponent(code)}?redirect=/cart`;
    });
  }

  function initWordRotate() {
    const el = document.querySelector('[data-word-rotate]');
    if (!el) return;
    const list = (el.dataset.words || el.textContent || '')
      .split(',')
      .map((w) => w.trim())
      .filter(Boolean);
    if (list.length < 2) return;
    let i = 0;
    el.textContent = list[0];
    el.style.transition = 'opacity 0.22s ease';
    setInterval(() => {
      i = (i + 1) % list.length;
      el.style.opacity = '0';
      setTimeout(() => {
        el.textContent = list[i];
        el.style.opacity = '1';
      }, 220);
    }, 2600);
  }

  function initAboutPage() {
    const page = document.querySelector('[data-about-page]');
    if (!page) return;
    const animated = page.querySelectorAll('[data-about-animate]');
    const counters = page.querySelectorAll('[data-count]');
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      animated.forEach((el) => el.classList.add('is-visible'));
      counters.forEach((el) => { el.textContent = `${el.dataset.count ?? ''}${el.dataset.suffix ?? ''}`; });
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        el.classList.add('is-visible');
        observer.unobserve(el);
      });
    }, { threshold: 0.12 });
    animated.forEach((el) => observer.observe(el));
  }

  function initPredictiveSearch() {
    const input = document.querySelector('[data-predictive-input]');
    const results = document.querySelector('[data-predictive-results]');
    if (!input || !results) return;
    let timer;
    input.addEventListener('input', () => {
      clearTimeout(timer);
      const q = input.value.trim();
      if (q.length < 2) { results.hidden = true; results.innerHTML = ''; return; }
      timer = setTimeout(async () => {
        try {
          const res = await fetch(`/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6`);
          const data = await res.json();
          const products = data.resources?.results?.products || [];
          if (!products.length) {
            results.hidden = false;
            results.innerHTML = '<p class="text-muted-luxury">No matches yet</p>';
            return;
          }
          results.hidden = false;
          results.innerHTML = products.map((p) => {
            const img = typeof p.featured_image === 'string' ? p.featured_image : (p.featured_image?.url || p.image || '');
            return `<a class="predictive-item" href="${p.url}">
              ${img ? `<img src="${img}" alt="">` : '<span></span>'}
              <span><strong>${p.title}</strong><br><span class="text-muted-luxury">${p.price || ''}</span></span>
            </a>`;
          }).join('');
        } catch {
          results.hidden = true;
        }
      }, 220);
    });
  }

  function initRecommendations() {
    document.querySelectorAll('[data-recommendations]').forEach(async (el) => {
      if (el.querySelector('.products-grid')) return;
      const url = el.dataset.url;
      if (!url) return;
      try {
        const res = await fetch(url);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const inner = doc.querySelector('[data-recommendations]');
        if (inner && inner.querySelector('.products-grid')) el.replaceWith(inner);
      } catch {
        /* ignore */
      }
    });
  }

  function initRecentlyViewed() {
    const page = document.querySelector('[data-product-page]');
    const key = 'evryn_recent';
    let items = [];
    try { items = JSON.parse(localStorage.getItem(key) || '[]'); } catch { items = []; }
    if (page) {
      const entry = {
        id: page.dataset.productId,
        url: page.dataset.productUrl,
        title: page.dataset.productTitle,
        image: page.dataset.productImage,
        ratio: page.dataset.productImageRatio || '',
      };
      items = [entry, ...items.filter((i) => i.id !== entry.id)].slice(0, 8);
      localStorage.setItem(key, JSON.stringify(items));
    }
    const section = document.querySelector('[data-recently-viewed]');
    const grid = section?.querySelector('[data-recent-grid]');
    if (!section || !grid) return;
    const others = items.filter((i) => i.id !== page?.dataset.productId).slice(0, 4);
    if (!others.length) return;
    section.hidden = false;
    grid.innerHTML = others.map((p) => {
      const ratio = p.ratio ? String(p.ratio) : '';
      const ratioAttr = ratio ? ` style="--media-ratio: ${ratio};"` : '';
      const safeTitle = String(p.title || '').replace(/[&<>"']/g, (ch) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
      }[ch]));
      return `
      <article class="product-card">
        <a href="${p.url}" class="product-card-link">
          <div class="product-image-wrap"${ratioAttr}>${p.image ? `<img class="product-image-primary" src="${p.image}" alt="${safeTitle}" loading="lazy">` : ''}</div>
          <h3 class="product-name">${safeTitle}</h3>
        </a>
      </article>`;
    }).join('');
    grid.querySelectorAll('.product-image-wrap img').forEach((img) => {
      const wrap = img.closest('.product-image-wrap');
      if (!wrap || wrap.style.getPropertyValue('--media-ratio')) return;
      const apply = () => {
        if (img.naturalWidth && img.naturalHeight) {
          wrap.style.setProperty('--media-ratio', String(img.naturalWidth / img.naturalHeight));
        }
      };
      if (img.complete && img.naturalWidth) apply();
      else img.addEventListener('load', apply, { once: true });
    });
  }

  function initShare() {
    document.querySelectorAll('[data-share-product]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const data = { title: btn.dataset.shareTitle, url: btn.dataset.shareUrl };
        try {
          if (navigator.share) await navigator.share(data);
          else {
            await navigator.clipboard.writeText(data.url);
            showToast('Link copied', 'success');
          }
        } catch {
          /* cancelled */
        }
      });
    });
  }

  function initGiftToggle() {
    const toggle = document.querySelector('[data-gift-toggle]');
    const fields = document.querySelector('[data-gift-fields]');
    if (!toggle || !fields) return;
    toggle.addEventListener('change', () => { fields.hidden = !toggle.checked; });
  }

  function initLocalization() {
    document.querySelectorAll('.localization-form select').forEach((select) => {
      select.addEventListener('change', () => select.form?.requestSubmit());
    });
  }

  function showToast(message, type = 'info') {
    let container = document.querySelector('.toast-container');
    if (!container) {
      container = document.createElement('div');
      container.className = 'toast-container';
      document.body.appendChild(container);
    }
    const toast = document.createElement('div');
    toast.className = `alert-luxury alert-${type === 'success' ? 'success' : type === 'error' ? 'error' : 'info'}`;
    toast.textContent = message;
    container.appendChild(toast);
    setTimeout(() => toast.remove(), 3800);
  }

  function initAccount() {
    document.querySelectorAll('[data-address-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const edit = btn.closest('.address-card')?.querySelector('.address-card__edit');
        if (edit) edit.hidden = !edit.hidden;
      });
    });
    document.querySelectorAll('select[name="address[country]"][data-default]').forEach((select) => {
      if (select.dataset.default) select.value = select.dataset.default;
    });
    document.querySelectorAll('[data-confirm]').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        if (!window.confirm(btn.dataset.confirm)) event.preventDefault();
      });
    });
  }
})();
